// SettingsAiFeedPane.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Services
import { updateSetting } from 'services/settings/SettingWriteService';

// Constants
import {
  AI_FEED_CATEGORY_OPTIONS,
  buildAiFeedSettingsPatch,
  resolveAiFeedPreferences,
} from 'spaces/constants/aiFeedConstants';

// CSS
import './SettingsAiFeedPane.css';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Switch from 'components/switch/Switch';

const SettingsAiFeedPane = React.memo(() => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { user, userSettings, setUserSettings } = useUser();
  const { activeBase } = useBase();
  const baseKey = String(activeBase?.key || '').trim();

  // ----- MEMOS ----- //

  const preferences = useMemo(
    () => resolveAiFeedPreferences(userSettings, baseKey),
    [userSettings, baseKey],
  );

  const persistPreferences = useCallback(async (patch) => {
    if (!user?.key || !baseKey) return;

    const settingsPatch = buildAiFeedSettingsPatch({
      userSettings,
      baseKey,
      patch,
    });

    if (!settingsPatch) return;

    setUserSettings((previous) => ({
      ...(previous || {}),
      ...settingsPatch,
    }));

    try {
      await updateSetting(user.key, settingsPatch);
    } catch (error) {
      console.error('[SettingsAiFeedPane] failed to save feed preferences:', error);
    }
  }, [setUserSettings, user?.key, userSettings, baseKey]);

  // ----- HANDLERS ----- //

  const handleDirectMessagesToggle = useCallback((directMessagesInFeed) => {
    persistPreferences({ directMessagesInFeed });
  }, [persistPreferences]);

  const handleCategoryToggle = useCallback((categoryKey, enabled) => {
    persistPreferences({
      categories: {
        [categoryKey]: enabled,
      },
    });
  }, [persistPreferences]);

  // ----- RENDER ----- //

  return (
    <>
      <SettingsModalPaneHeader
        title="Appdeck AI"
        subtitle="Choose what AI posts in your base conversation."
      />

      <SettingsModalPaneContents>
        {/* AI SETTINGS */}
        <div className="settings-modal-ai-settings">

          <div className="settings-modal-theme-card">
            <div className="settings-modal-theme-meta">
              <div
                className="settings-modal-setting-label"
                style={{
                  color: theme.foreground,
                }}
              >
                Include direct messages in your feed
              </div>
              <div
                className="settings-modal-setting-description"
                style={{
                  color: theme.foregroundMuted,
                }}
              >
                Centralizes your DM conversations with Ai.
              </div>
            </div>
            <Switch
              checked={preferences.directMessagesInFeed}
              onChange={handleDirectMessagesToggle}
            />
          </div>

          {AI_FEED_CATEGORY_OPTIONS.map((option) => (
            <div className="settings-modal-theme-card" key={option.key}>
              <div className="settings-modal-theme-meta">
                <div
                  className="settings-modal-setting-label"
                  style={{
                    color: theme.foreground,
                  }}
                >
                  {option.label}
                </div>
                <div
                  className="settings-modal-setting-description"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >
                  {option.description}
                </div>
              </div>
              <Switch
                checked={preferences.categories?.[option.key] !== false}
                disabled={!preferences.enabled}
                onChange={(enabled) => handleCategoryToggle(option.key, enabled)}
              />
            </div>
          ))}

        </div>
      </SettingsModalPaneContents>
    </>
  );
});

// Dev Tools
SettingsAiFeedPane.displayName = 'SettingsAiFeedPane';

export default SettingsAiFeedPane;
